import * as React from "react";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs" with { type: "file" };
import {
	ChevronLeft,
	ChevronRight,
	ExternalLink,
	FileText,
	Loader2,
	ScanSearch,
	ZoomIn,
	ZoomOut,
} from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

const DOCUMENT_PREVIEW_PROXY_PATH = "/api/document-preview-proxy";
const DEFAULT_PAGE_WIDTH = 640;
const MIN_PAGE_WIDTH = 240;
const MAX_PAGE_WIDTH = 960;
const PAGE_GUTTER = 24;
const MIN_ZOOM = 0.75;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.25;
const LENS_SIZE = 176;
const LENS_MAGNIFICATION = 2.5;
const MAX_RENDER_DEVICE_PIXEL_RATIO = 2;

function buildDocumentPreviewProxyUrl(url: string): string {
	return `${DOCUMENT_PREVIEW_PROXY_PATH}?${new URLSearchParams({ url }).toString()}`;
}

function clamp(value: number, minimum: number, maximum: number): number {
	return Math.min(Math.max(value, minimum), maximum);
}

export function drawMagnifiedRegion({
	sourceCanvas,
	lensCanvas,
	clientX,
	clientY,
}: {
	sourceCanvas: HTMLCanvasElement;
	lensCanvas: HTMLCanvasElement;
	clientX: number;
	clientY: number;
}): { left: number; top: number } | null {
	const sourceRect = sourceCanvas.getBoundingClientRect();
	if (
		sourceRect.width <= 0 ||
		sourceRect.height <= 0 ||
		sourceCanvas.width <= 0 ||
		sourceCanvas.height <= 0
	) {
		return null;
	}

	const localX = clamp(clientX - sourceRect.left, 0, sourceRect.width);
	const localY = clamp(clientY - sourceRect.top, 0, sourceRect.height);
	const sourceScaleX = sourceCanvas.width / sourceRect.width;
	const sourceScaleY = sourceCanvas.height / sourceRect.height;
	const sourceWidth = (LENS_SIZE / LENS_MAGNIFICATION) * sourceScaleX;
	const sourceHeight = (LENS_SIZE / LENS_MAGNIFICATION) * sourceScaleY;
	const sourceX = clamp(
		localX * sourceScaleX - sourceWidth / 2,
		0,
		Math.max(0, sourceCanvas.width - sourceWidth),
	);
	const sourceY = clamp(
		localY * sourceScaleY - sourceHeight / 2,
		0,
		Math.max(0, sourceCanvas.height - sourceHeight),
	);
	const devicePixelRatio = Math.min(window.devicePixelRatio || 1, 2);
	const targetSize = Math.round(LENS_SIZE * devicePixelRatio);
	if (lensCanvas.width !== targetSize || lensCanvas.height !== targetSize) {
		lensCanvas.width = targetSize;
		lensCanvas.height = targetSize;
	}

	const context = lensCanvas.getContext("2d");
	if (!context) return null;
	context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
	context.clearRect(0, 0, LENS_SIZE, LENS_SIZE);
	context.imageSmoothingEnabled = true;
	context.imageSmoothingQuality = "high";
	context.drawImage(
		sourceCanvas,
		sourceX,
		sourceY,
		sourceWidth,
		sourceHeight,
		0,
		0,
		LENS_SIZE,
		LENS_SIZE,
	);

	return {
		left: clamp(localX - LENS_SIZE / 2, 0, Math.max(0, sourceRect.width - LENS_SIZE)),
		top: clamp(localY - LENS_SIZE / 2, 0, Math.max(0, sourceRect.height - LENS_SIZE)),
	};
}

export function PdfDocumentViewer({
	sourceUrl,
	filename,
	onPageCountChange,
}: {
	sourceUrl: string;
	filename: string;
	onPageCountChange?: (pageCount: number) => void;
}) {
	const viewportRef = React.useRef<HTMLDivElement>(null);
	const sourceCanvasRef = React.useRef<HTMLCanvasElement | null>(null);
	const lensCanvasRef = React.useRef<HTMLCanvasElement | null>(null);
	const lensRef = React.useRef<HTMLDivElement>(null);
	const [numPages, setNumPages] = React.useState(0);
	const [pageNumber, setPageNumber] = React.useState(1);
	const [zoom, setZoom] = React.useState(1);
	const [pageWidth, setPageWidth] = React.useState(DEFAULT_PAGE_WIDTH);
	const [lensEnabled, setLensEnabled] = React.useState(true);
	const [viewerMode, setViewerMode] = React.useState<"magnifier" | "browser">(
		"magnifier",
	);

	const previewUrl = React.useMemo(
		() => buildDocumentPreviewProxyUrl(sourceUrl),
		[sourceUrl],
	);
	const renderDevicePixelRatio = Math.min(
		window.devicePixelRatio || 1,
		MAX_RENDER_DEVICE_PIXEL_RATIO,
	);

	React.useEffect(() => {
		const viewport = viewportRef.current;
		if (!viewport) return;

		const updateWidth = (width: number) => {
			setPageWidth(
				clamp(Math.floor(width - PAGE_GUTTER), MIN_PAGE_WIDTH, MAX_PAGE_WIDTH),
			);
		};
		updateWidth(viewport.clientWidth);

		const observer = new ResizeObserver((entries) => {
			const entry = entries[0];
			if (entry) updateWidth(entry.contentRect.width);
		});
		observer.observe(viewport);
		return () => observer.disconnect();
	}, []);

	const changeZoom = (amount: number) => {
		if (lensRef.current) lensRef.current.hidden = true;
		setZoom((current) =>
			clamp(Number((current + amount).toFixed(2)), MIN_ZOOM, MAX_ZOOM),
		);
	};

	const changePage = (nextPage: number) => {
		if (lensRef.current) lensRef.current.hidden = true;
		setPageNumber(clamp(nextPage, 1, numPages));
	};

	const hideLens = () => {
		if (lensRef.current) lensRef.current.hidden = true;
	};

	const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
		if (event.pointerType !== "mouse" || !lensEnabled) {
			hideLens();
			return;
		}
		const sourceCanvas = sourceCanvasRef.current;
		const lensCanvas = lensCanvasRef.current;
		const lens = lensRef.current;
		if (!sourceCanvas || !lensCanvas || !lens) return;

		const position = drawMagnifiedRegion({
			sourceCanvas,
			lensCanvas,
			clientX: event.clientX,
			clientY: event.clientY,
		});
		if (!position) return;
		lens.style.transform = `translate3d(${position.left}px, ${position.top}px, 0)`;
		lens.hidden = false;
	};

	const pageStatus = numPages > 0 ? `Page ${pageNumber} of ${numPages}` : "Loading pages";
	if (viewerMode === "browser") {
		return (
			<div className="flex h-full min-h-96 flex-col bg-muted/20">
				<div className="flex flex-wrap items-center justify-center gap-2 border-b bg-background/95 p-2">
					<Button
						type="button"
						variant="secondary"
						size="sm"
						onClick={() => setViewerMode("magnifier")}
					>
						<ScanSearch />
						Use magnifier view
					</Button>
					<Button variant="outline" size="sm" asChild>
						<a href={sourceUrl} target="_blank" rel="noopener noreferrer">
							<ExternalLink />
							Open in new tab
						</a>
					</Button>
				</div>
				<iframe
					src={`${sourceUrl}#view=FitH`}
					title={`${filename} browser preview`}
					className="min-h-0 flex-1"
				/>
			</div>
		);
	}

	return (
		<div className="flex h-full min-h-96 flex-col bg-muted/20">
			<div
				role="toolbar"
				aria-label="PDF controls"
				className="flex flex-wrap items-center justify-center gap-1.5 border-b bg-background/95 p-2"
			>
				<Button
					type="button"
					variant="outline"
					size="icon-sm"
					aria-label="Previous page"
					disabled={pageNumber <= 1}
					onClick={() => changePage(pageNumber - 1)}
				>
					<ChevronLeft />
				</Button>
				<span aria-live="polite" className="min-w-24 text-center text-xs tabular-nums">
					{pageStatus}
				</span>
				<Button
					type="button"
					variant="outline"
					size="icon-sm"
					aria-label="Next page"
					disabled={numPages === 0 || pageNumber >= numPages}
					onClick={() => changePage(pageNumber + 1)}
				>
					<ChevronRight />
				</Button>

				<span aria-hidden="true" className="mx-0.5 h-5 w-px bg-border" />

				<Button
					type="button"
					variant="outline"
					size="icon-sm"
					aria-label="Zoom out"
					disabled={zoom <= MIN_ZOOM}
					onClick={() => changeZoom(-ZOOM_STEP)}
				>
					<ZoomOut />
				</Button>
				<span aria-live="polite" className="min-w-11 text-center text-xs tabular-nums">
					{Math.round(zoom * 100)}%
				</span>
				<Button
					type="button"
					variant="outline"
					size="icon-sm"
					aria-label="Zoom in"
					disabled={zoom >= MAX_ZOOM}
					onClick={() => changeZoom(ZOOM_STEP)}
				>
					<ZoomIn />
				</Button>
				<Button
					type="button"
					variant="outline"
					size="sm"
					className="h-8"
					onClick={() => {
						hideLens();
						setViewerMode("browser");
					}}
				>
					<FileText />
					Browser view
				</Button>
				<Button
					type="button"
					variant={lensEnabled ? "secondary" : "outline"}
					size="icon-sm"
					aria-label={lensEnabled ? "Turn magnifier off" : "Turn magnifier on"}
					aria-pressed={lensEnabled}
					title={lensEnabled ? "Magnifier on" : "Magnifier off"}
					onClick={() => {
						setLensEnabled((current) => !current);
						hideLens();
					}}
				>
					<ScanSearch />
				</Button>
				<Button variant="outline" size="icon-sm" asChild>
					<a
						href={sourceUrl}
						target="_blank"
						rel="noopener noreferrer"
						aria-label="Open PDF in a new tab"
						title="Open PDF in a new tab"
					>
						<ExternalLink />
					</a>
				</Button>
			</div>

			<p className="border-b bg-background px-3 py-1.5 text-center text-xs text-muted-foreground">
				{lensEnabled
					? "Hover over the page to magnify details."
					: "Magnifier is off. Use the magnifier button to turn it on."}
			</p>

			<div ref={viewportRef} className="min-h-0 flex-1 overflow-auto p-3">
				<Document
					key={previewUrl}
					file={previewUrl}
					onLoadSuccess={({ numPages: loadedPages }) => {
						setNumPages(loadedPages);
						onPageCountChange?.(loadedPages);
						setPageNumber((current) => clamp(current, 1, loadedPages));
					}}
					loading={
						<div className="flex min-h-80 items-center justify-center" role="status">
							<Loader2 className="mr-2 size-5 animate-spin" />
							Loading PDF…
						</div>
					}
					error={
						<div className="flex min-h-80 flex-col items-center justify-center gap-3 p-6 text-center text-sm">
							<p className="text-muted-foreground">This PDF could not be previewed here.</p>
							<Button variant="outline" size="sm" asChild>
								<a href={sourceUrl} target="_blank" rel="noopener noreferrer">
									Open PDF in a new tab
								</a>
							</Button>
						</div>
					}
					className="flex min-h-full justify-center"
				>
					<div
						className="relative w-fit overflow-hidden rounded-sm bg-white shadow-sm ring-1 ring-black/10"
						onPointerMoveCapture={handlePointerMove}
						onPointerLeave={hideLens}
						aria-label={`${filename}, ${pageStatus}`}
					>
						<Page
							pageNumber={pageNumber}
							width={pageWidth * zoom}
							devicePixelRatio={renderDevicePixelRatio}
							canvasRef={sourceCanvasRef}
							renderAnnotationLayer
							renderTextLayer
							loading={
								<div className="flex min-h-80 items-center justify-center px-8 text-sm text-muted-foreground">
									Rendering page…
								</div>
							}
						/>
						<div
							ref={lensRef}
							hidden
							aria-hidden="true"
							className={cn(
								"pointer-events-none absolute left-0 top-0 overflow-hidden rounded-full",
								"size-44 border-2 border-background bg-white shadow-xl ring-1 ring-foreground/30",
							)}
						>
							<canvas ref={lensCanvasRef} className="block size-44" />
							<span className="absolute inset-x-0 top-1/2 h-px bg-foreground/25" />
							<span className="absolute inset-y-0 left-1/2 w-px bg-foreground/25" />
						</div>
					</div>
				</Document>
			</div>
		</div>
	);
}
